"use client";

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderOptionState,
  Box,
  Button,
  Container,
  Popper,
  TextField,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import SubArrowIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import UncheckedIcon from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckedIcon from "@mui/icons-material/CheckBoxRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import { DateRangePicker, FocusedInputShape, OrientationShape } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import moment from "moment";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { DestinationIcon } from "../icons/DestinationIcon";
import { HarbourPortIcon } from "../icons/HarbourPortIcon";
import { DateRangeIcon } from "../icons/DateRangeIcon";
import { CruiseTypeIcon } from "../icons/CruiseTypeIcon";
import { CruiseLineIcon } from "../icons/CruiseLineIcon";
import { SearchIcon } from "../icons/SearchIcon";

//need to chenge json after live key add
import departurePortsJson from "@/assets/json/ports.json";
import cruiseShips from "@/assets/json/ships.json";
import destinationJson from "@/assets/json/destination_with_cL.json";

import { ICruisesOperators } from "@/interfaces/ICruisesOperators";
import { getCruiseOperators } from "@/services/backOffice";

type onDatesChange = { startDate: moment.Moment | null; endDate: moment.Moment | null };

type Port = {
  name: string;
  country: string;
  operator: string[];
  id: number;
};

const destinations = destinationJson;

export const cruiseTypes = [
  { label: "Any", value: 0, name: "" },
  { label: "Ocean Cruise", value: 1, name: "ocean" },
  { label: "River Cruise", value: 2, name: "river" },
  // { label: "Luxury Cruise", value: 3 },
  // { label: "Expedition Cruise", value: 4 },
  // { label: "Family Cruise", value: 5 },
  // { label: "Adults-Only Cruise", value: 6 },
  // { label: "Theme Cruise", value: 7 },
  // { label: "Adventure Cruise", value: 8 },
  // { label: "World Cruise", value: 9 },
  // { label: "Short Cruise", value: 10 },
  // { label: "Repositioning Cruise", value: 11 },
  // { label: "Singles Cruise", value: 12 },
  // { label: "Honeymoon Cruise", value: 13 },
  // { label: "Wellness & Spa Cruise", value: 14 },
  // { label: "Eco-Cruise", value: 15 },
  // { label: "Cultural Cruise", value: 16 },
  // { label: "Gastronomy & Wine Cruise", value: 17 },
  // { label: "Polar Cruise (Arctic & Antarctica)", value: 18 },
  // { label: "Music & Festival Cruise", value: 19 },
  // { label: "Casino Cruise", value: 20 },
];

export interface ISearchForm {
  destination: {
    value: number;
    label: string;
    req_url: string;
    type: string;
    cruiseline: string[];
  } | null; // (Target) Region
  departurePort: {
    country: string;
    name: string;
    id: number;
    operator: string[];
  } | null; // startFrom (Harbour Port)
  leavingStartDate: string | null; // StartDateRangeBeginning (Date)
  leavingEndDate: string | null; // StartDateRangeEnd (Date)
  cruiseType: {
    value: number;
    label: string;
    name: string;
  } | null; // cruiseType (River or Ocean)
  cruiseOperator: {
    isAvailable: boolean;
    id: string;
    profileImageHref: string;
    shipsCount?: number;
    title: string;
  } | null; // Operator Cruise Line (amawaterways)
  cruiseShip: {
    title: string;
    operator: {
      name: string;
      profileImageHref: string;
    };
    href: string;
  } | null; // ship_name (AmaBella)
  isAdvanceSearch: boolean;
}

const searchSchema = yup.object({
  destination: yup
    .object({
      value: yup.number(),
      label: yup.string(),
      Ports: yup.string(),
    })
    .nullable(),

  departureStartDate: yup.string(),
  departureEndDate: yup.string(),
  cruiseType: yup
    .object({
      value: yup.number(),
      label: yup.string(),
    })
    .nullable(),

  isAdvanceSearch: yup.boolean(),
});

const SearchLayout: React.FC<{ isModifySearch?: boolean }> = ({ isModifySearch }) => {
  // const router = useRouter();

  // Todo false move to live
  const [isShowAdvanceFields, setIsShowAdvanceFields] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    // formState: { errors },
    setValue,
    // clearErrors,
    // setError,
    // reset,
  } = useForm<ISearchForm>({
    // @ts-expect-error Type-Error
    resolver: yupResolver(searchSchema),
    mode: "onSubmit",
    defaultValues: {
      destination: null,
      departurePort: null,
      leavingStartDate: null,
      leavingEndDate: null,
      cruiseType: null,
      cruiseOperator: null,
      cruiseShip: null,
      isAdvanceSearch: false,
    },
  });

  const [cruiseLines, setOperators] = useState<ICruisesOperators>();
  const [departurePorts, setDeparturePorts] = useState<Port[]>([]);

  const params = useSearchParams();

  const [startDate, setStartDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);

  // Then use params inside useEffect
  useEffect(() => {
    // Extract parameters
    const region = params.get("region");
    const startFrom = params.get("startfrom");
    const rangeBeginning = params.get("rangeBeginning");
    const rangeEnd = params.get("rangeEnd");
    const operator = params.get("operator");
    const shipName = params.get("shipName");
    const ctype = params.get("ctype");
    // Set form values based on URL parameters
    if (region) {
      const matchedDestination = destinations.regions.find(
        (dest) => dest.req_url.toLowerCase() === region.toLowerCase(),
      );
      if (matchedDestination) {
        setValue("destination", matchedDestination);
      }
    }

    if (startFrom && departurePorts.length > 0) {
      const matchedPort = departurePorts.find((port) =>
        port.name.toLowerCase().includes(startFrom.toLowerCase()),
      );
      if (matchedPort) {
        setValue("departurePort", matchedPort);
      }
    }

    if (rangeBeginning) {
      setValue("leavingStartDate", rangeBeginning);
      const StartdateMoment = moment(rangeBeginning);
      if (StartdateMoment.isValid()) {
        setStartDate(StartdateMoment);
      }
    }

    if (rangeEnd) {
      setValue("leavingEndDate", rangeEnd);
      const endDateMoment = moment(rangeEnd);
      if (endDateMoment.isValid()) {
        setEndDate(endDateMoment);
      }
    }

    if (ctype) {
      const matchedctype = cruiseTypes.find((op) => op.name === ctype);
      if (matchedctype) {
        setValue("cruiseType", {
          label: matchedctype.label,
          name: matchedctype.name,
          value: matchedctype.value,
        });
      }
    }

    if (shipName && cruiseShips) {
      const matchedShip = cruiseShips.find(
        (ship) => ship.title.toLowerCase() === shipName.toLowerCase(),
      );
      if (matchedShip) {
        setValue("cruiseShip", matchedShip);
      }
    }

    // For the operator
    if (operator && cruiseLines?.operatorDetails) {
      let matchedOperator = cruiseLines.operatorDetails.find((op) => op.id === operator); // Try direct ID match first

      if (!matchedOperator) {
        matchedOperator = cruiseLines.operatorDetails.find(
          (op) => formatUrlString(op.title) === operator.toLowerCase(),
        );
      }

      if (matchedOperator) {
        setValue("cruiseOperator", {
          id: matchedOperator.id,
          title: matchedOperator.title,
          profileImageHref: matchedOperator.profileImageHref,
          isAvailable: false,
        });
      }
    }

    // For the ship name
    if (shipName && cruiseShips) {
      let matchedShip = cruiseShips.find(
        (ship) => ship.title.toLowerCase() === shipName.toLowerCase(),
      );

      if (!matchedShip) {
        matchedShip = cruiseShips.find(
          (ship) => formatUrlString(ship.title) === shipName.toLowerCase(),
        );
      }
      if (!matchedShip) {
        matchedShip = cruiseShips.find((ship) =>
          ship.title.toLowerCase().includes(shipName.replace(/-/g, " ").toLowerCase()),
        );
      }

      if (matchedShip) {
        setValue("cruiseShip", matchedShip);
      }
    }

    if (startFrom && departurePorts.length > 0) {
      const decodedPort = decodeURIComponent(startFrom);
      let matchedPort = departurePorts.find((port) => port.name === decodedPort);
      if (!matchedPort) {
        matchedPort = departurePorts.find((port) =>
          port.name.toLowerCase().includes(decodedPort.toLowerCase()),
        );
      }
      if (matchedPort) {
        setValue("departurePort", matchedPort);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params,
    destinations,
    departurePorts,
    cruiseLines,
    cruiseShips,
    cruiseTypes,
    setValue,
    setStartDate,
    setEndDate,
    setIsShowAdvanceFields,
  ]);

  // cruisline
  useLayoutEffect(() => {
    const fetchData = async () => {
      const { data } = await getCruiseOperators(100, 1, true); //45 ad 100
      const sortedOperators = {
        ...data,
        operatorDetails: data?.operatorDetails?.sort((a, b) => a.title.localeCompare(b.title)),
      };
      setOperators(sortedOperators);
    };
    fetchData();
  }, []);

  // its change spcae with + change
  function formatUrlString(input: string) {
    let result = input.replace(/[\s+&]/g, "-").toLowerCase();
    result = result.replace(/-{2,}/g, "-");
    return result;
  }
  function formatUrlSpace(input: string) {
    const result = input.replace(/[\s+&]/g, " ");
    return result;
  }

  const onSubmit: SubmitHandler<ISearchForm> = (data) => {
    // Create a URL
    const urlParams = new URLSearchParams();

    //  if they exist
    if (data?.destination?.label && data?.destination?.label !== "Any Regions")
      urlParams.append("region", data.destination.req_url); //region
    if (data?.departurePort?.name) urlParams.append("startfrom", data.departurePort.name); //ports
    if (data.leavingStartDate && data.leavingStartDate !== "null")
      urlParams.append("rangeBeginning", data.leavingStartDate); //2025-05-20  or  2025/05/01
    if (data.leavingEndDate && data.leavingEndDate !== "null")
      urlParams.append("rangeEnd", data.leavingEndDate); // 2025-05-20  or 2025/05/01
    if (data?.cruiseOperator?.id) {
      urlParams.append("operator", data?.cruiseOperator?.id); //to cruiseline
    }
    if (data?.cruiseShip?.title) {
      const formattedcruiseShip = formatUrlSpace(data.cruiseShip.title);
      urlParams.append("shipName", formattedcruiseShip);
    } // ships route
    if (data?.cruiseType?.name) urlParams.append("ctype", data.cruiseType.name); // ctype route

    // Generate the URL
    const queryString = urlParams.toString();
    const routeURL = `/cruise-results${queryString ? "?" + queryString : ""}`;

    window.location.href = routeURL;
  };

  // Add meme speed up search only on auto complete
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setDeparturePorts(departurePortsJson);
  }, []);

  const selectCruiseLine = watch("cruiseOperator");
  const [portSearchQuery, setPortSearchQuery] = useState("");
  const [shipSearchQuery, setShipSearchQuery] = useState("");

  const destinationValue = watch("destination");
  const DeparturePortValue = watch("departurePort");

  const filteredCruiseLines = useMemo(() => {
    if (!cruiseLines?.operatorDetails) {
      return [];
    }

    const selectedPort = watch("departurePort")?.operator;
    const selectedRegion = watch("destination");
    if (
      (selectedPort && selectedPort.length === 0) ||
      (selectedRegion && selectedRegion.value === 1) ||
      (!selectedRegion && !selectedPort) ||
      (selectedRegion && selectedRegion.cruiseline.length === 0)
    ) {
      return cruiseLines.operatorDetails.map((cruiseLine) => ({
        ...cruiseLine,
        isAvailable: true,
      }));
    }

    const regionData = destinations?.regions?.find(
      (region) => region.value === selectedRegion?.value,
    );
    const regionCruiseLines = new Set(regionData?.cruiseline?.map((id) => id.toLowerCase()));

    const availableOperatorIds = new Set(selectedPort?.map((id) => id.toLowerCase()));

    const result = cruiseLines?.operatorDetails?.map((cruiseLine) => {
      const cruiseLineId = cruiseLine.id.toLowerCase();
      const isAvailable =
        availableOperatorIds?.has(cruiseLineId) || regionCruiseLines?.has(cruiseLineId);

      return {
        ...cruiseLine,
        isAvailable,
      };
    });

    // Sort to show available cruise lines first
    return result?.sort((a, b) => {
      if (a.isAvailable && !b.isAvailable) return -1;
      if (!a.isAvailable && b.isAvailable) return 1;
      return a.title.localeCompare(b.title);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cruiseLines, DeparturePortValue, destinationValue]);

  const filteredDeparturePorts = useMemo(() => {
    let filteredPorts = [...departurePorts];

    const selectedCruiseLine = watch("cruiseOperator");

    if (selectedCruiseLine && selectedCruiseLine.id) {
      filteredPorts = filteredPorts.filter((port) => port.operator.includes(selectedCruiseLine.id));
    }

    if (portSearchQuery && portSearchQuery.trim() !== "") {
      const query = portSearchQuery.toLowerCase().trim();
      filteredPorts = filteredPorts.filter(
        (port) =>
          port.name.toLowerCase().includes(query) || port.country.toLowerCase().includes(query),
      );
    }

    // Always sort results by country
    const result = filteredPorts.sort((a, b) => a.country.localeCompare(b.country));

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portSearchQuery, watch("cruiseOperator"), focused, departurePorts]);

  const filteredCruiseShips = useMemo(() => {
    const selectedCruiseLine = watch().cruiseOperator;

    let filteredShips = [...cruiseShips];
    // If cruise line selected,
    if (selectedCruiseLine && selectedCruiseLine.title) {
      filteredShips = filteredShips.filter(
        (ship) => ship.operator.name === selectedCruiseLine.title,
      );
    }

    // apply search query
    if (shipSearchQuery && shipSearchQuery.trim() !== "") {
      const query = shipSearchQuery.toLowerCase().trim();
      filteredShips = filteredShips.filter(
        (ship) =>
          ship.title.toLowerCase().includes(query) ||
          ship.operator.name.toLowerCase().includes(query),
      );
    }

    return filteredShips.sort((a, b) => a.operator.name.localeCompare(b.operator.name));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipSearchQuery, focused, selectCruiseLine]);

  const lessThanMD = useMediaQuery("(max-width:900px)");
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (focused && formRef.current && !lessThanMD) {
      setTimeout(() => {
        if (formRef.current) {
          // const formRect = formRef.current.getBoundingClientRect();
          // const currentScrollY = window.scrollY;
          // const targetScrollPosition = currentScrollY + formRect.top - 10;
          window.scrollTo({
            // top: targetScrollPosition,
            top: isModifySearch ? 0 : 140,
            behavior: "smooth",
          });
        }
      }, 10);
      setFocused(true);
    }
  }, [focused, lessThanMD, isModifySearch]);

  // Handle click outside to close the focus effect
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setFocused(false);
      }
    };
    if (focused) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focused]);

  const handleFormFocus = () => {
    setFocused(true);
  };

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);

  const handleDatesChange = ({ startDate, endDate }: onDatesChange) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate) {
      setValue("leavingStartDate", startDate.format("YYYY-MM-DD"));
    }
    if (endDate) {
      setValue("leavingEndDate", endDate.format("YYYY-MM-DD"));
    }
  };

  return (
    <>
      {focused && !lessThanMD && (
        <Box
          onClick={() => setFocused(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 12,
            transition: "all 0.3s ease",
          }}
        />
      )}

      <Box
        component="form"
        ref={formRef}
        onFocus={handleFormFocus}
        onSubmit={handleSubmit(onSubmit, (err) => console.log("ERR", err))}
        sx={{
          position: "relative",
          zIndex: 12,
          mt: { xs: "-1rem", md: "0rem" },
          ".MuiContainer-root.MuiContainer-maxWidthXl": {
            maxWidth: { xs: "1300px", lg: "1300px" },
          },
        }}
      >
        <Container
          sx={{
            "@media screen and (max-width: 600px)": {
              p: "0px",
            },
          }}
        >
          <Box
            sx={{
              p: { xs: "12px 12px 50px", sm: "8px 6px 6px", md: "10px" },
              bgcolor: "#fff",
              borderRadius: { xs: "20px 20px 0px 0px", sm: "15px", md: "20px" },
              border: "4px solid #fff7",
              boxShadow: { xs: "unset", md: "0px 0px 14px rgba(0,0,0,0.3)" },
              position: "relative",
            }}
          >
            <Grid container spacing={{ xs: 1.5, md: 2 }} columnSpacing={{ xs: 1, md: 2 }}>
              <Grid
                container
                spacing={{ xs: 1.5, md: 2 }}
                columnSpacing={{ xs: 1, md: 2 }}
                size={{ xs: 12, sm: 12, md: 10.2 }}
              >
                <Grid size={{ xs: 12, md: 6, lg: isShowAdvanceFields ? 3.9 : 3.1 }} order={1}>
                  <CommonFieldUI icon={<DestinationIcon />} label="Destination Region">
                    <Box
                      sx={{
                        ".MuiInputBase-root.Mui-focused.MuiAutocomplete-inputRoot": {
                          "&::after": {
                            borderBottom: "none",
                          },
                          "&::before": {
                            borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                          },
                          input: {
                            p: "3px 0px 0px 0px",
                            fontSize: "15px",
                            fontFamily: "'blauerNue', 'blauerNue Fallback'",
                          },
                        },
                      }}
                    >
                      <Controller
                        name="destination"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            onFocus={() => setFocused(true)}
                            disablePortal
                            options={destinations.regions}
                            fullWidth
                            onChange={(e, val) => {
                              setValue("destination", val);
                            }}
                            value={field.value}
                            getOptionLabel={(option) => option.label}
                            renderInput={(params) => (
                              <TextField
                                label=""
                                placeholder="Where to go?"
                                variant="standard"
                                focused
                                {...params}
                              />
                            )}
                            groupBy={(group) => group.type}
                            renderGroup={(params) => destinationAutocompleteRenderGroup(params)}
                            renderOption={(props, option, state) =>
                              renderOptionUIV2(props, option, state, <SubArrowIcon />)
                            }
                            slotProps={{
                              paper: {
                                sx: {
                                  width: { xs: "calc(100% + 58px)", md: "300px" },
                                  boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                                  borderRadius: "0px 0px 14px 14px",
                                  ml: { xs: "-58px", md: "-35px" },
                                  mt: "3px",
                                },
                              },
                            }}
                            slots={{
                              popper: (props) => (
                                <Popper
                                  {...props}
                                  placement="bottom-start"
                                  modifiers={[
                                    {
                                      name: "flip",
                                      enabled: false,
                                    },
                                  ]}
                                />
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                  </CommonFieldUI>
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: isShowAdvanceFields ? 3.9 : 2.9 }} order={2}>
                  <CommonFieldUI icon={<CruiseLineIcon />} label="Cruise Line">
                    <Box
                      sx={{
                        ".MuiInputBase-root.Mui-focused.MuiAutocomplete-inputRoot": {
                          "&::after": {
                            borderBottom: "none",
                          },
                          "&::before": {
                            borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                          },
                          input: {
                            p: "3px 0px 0px 0px",
                            fontSize: "15px",
                            fontFamily: "'blauerNue', 'blauerNue Fallback'",
                          },
                        },
                      }}
                    >
                      <Controller
                        name="cruiseOperator"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            onFocus={() => setFocused(true)}
                            disablePortal
                            // options={cruiseLines?.operatorDetails || []}
                            //  options={cruiseLinesWithAvailability} /
                            options={filteredCruiseLines}
                            fullWidth
                            onChange={(e, val) => {
                              if (val) {
                                const operatorValue = {
                                  id: val.id,
                                  title: val.title,
                                  profileImageHref: val.profileImageHref,
                                  isAvailable: val.isAvailable,
                                  // shipsCount: val, // Make sure to include this property
                                };
                                setValue("cruiseOperator", operatorValue);
                              } else {
                                setValue("cruiseOperator", null);
                              }
                            }}
                            //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            //@ts-ignore
                            value={field.value}
                            sx={{}}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            getOptionDisabled={(option) => !option.isAvailable} // Disable unavailable options
                            getOptionLabel={(option) => option.title || ""}
                            // getOptionLabel={(option) => option.title || ""}
                            renderOption={(props, option, state) =>
                              renderOptionUIV2(props, option, state)
                            }
                            renderInput={(params) => (
                              <TextField
                                label=""
                                placeholder="Pick cruise operator"
                                variant="standard"
                                focused
                                {...params}
                              />
                            )}
                            slotProps={{
                              paper: {
                                sx: {
                                  width: { xs: "calc(100% + 58px)", md: "300px" },
                                  boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                                  borderRadius: "0px 0px 14px 14px",
                                  ml: { xs: "-58px", md: "-12px" },
                                  mt: "3px",
                                },
                              },
                            }}
                            slots={{
                              popper: (props) => (
                                <Popper
                                  {...props}
                                  placement="bottom-start"
                                  modifiers={[
                                    {
                                      name: "flip",
                                      enabled: false,
                                    },
                                  ]}
                                />
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                  </CommonFieldUI>
                </Grid>

                <Grid
                  size={{ xs: 12, md: 6, lg: isShowAdvanceFields ? 4.2 : 3.1 }}
                  order={isShowAdvanceFields ? 3 : 4}
                >
                  <CommonFieldUI icon={<DateRangeIcon />} label="Embarkation Date Range">
                    <Box sx={{}}>
                      {" "}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: { xs: "space-between", md: "flex-start" },

                          ".DateRangePicker": {
                            //input style
                            ".DateInput_input": {
                              color: "#111",
                              textAlign: "left",
                              p: "2px 0px",
                              pt: "4px",
                              fontSize: "14px !important",
                              fontFamily: "'blauerNue', 'blauerNue Fallback'",
                              fontWeight: "400 !important",
                              borderBottom: "1px solid #CCCCCC",
                              "&::placeholder": {
                                fontWeight: 300,
                                color: "#A2A2A2",
                                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                              },
                            },
                            ".DateRangePickerInput": {
                              display: "grid !important",
                              gridTemplateColumns: "1fr 1fr",
                              bgcolor: "#fff",
                              height: "100%",
                              gap: "16px !important",
                              ".DateInput": {
                                background: "#fff",
                                width: { xs: "100% !important", md: "100% !important" },
                                height: "100% !important",
                                "&:first-of-type": {
                                  position: "relative",
                                  "&:after": {
                                    content: '" _ "',
                                    color: "#757575",
                                    position: "absolute",
                                    right: "-10px",
                                    top: "-3px",
                                    zIndex: 1,
                                  },
                                },
                              },
                              ".DateInput > input": {
                                height: "100% !important",
                                bgcolor: "#fff",
                              },
                              ".DateRangePickerInput_arrow": {
                                display: "none !important",
                              },
                            },
                            ".DateInput_input__disabled": {
                              fontStyle: "initial",
                            },
                            ".DateInput_input__focused": {
                              borderBottom: "none",
                            },
                            ".DateRangePickerInput_clearDates": {
                              bgcolor: "#fff",
                              color: "#A2A2A2",
                              borderRadius: "10px",
                              p: "3px 3px",
                              minWidth: "unset",
                              top: "10px",
                              right: { xs: "-16px", md: "-16px" },
                            },
                            ".DateRangePickerInput__showClearDates": {
                              pr: "0px !important",
                            },
                          },

                          //Calander
                          ".DateInput_fang": {
                            top: "28px !important",
                          },
                          ".DateRangePicker_picker__directionLeft_2": {
                            top: "38px !important",
                            left: "50% !important",
                            transform: "translateX(-70%) !important",
                            padding: "16px 16px 0px 16px",
                            borderRadius: "16px",
                            boxShadow: "0 2px 6px rgba(0,0,0,.05),0 0 0 1px rgba(0,0,0,.07)",
                            overflow: "hidden",
                          },
                          ".DayPicker ": {
                            width: "800px !important",
                          },
                          ".DayPicker__withBorder": {
                            boxShadow: "none",
                          },
                          ".DayPicker_weekHeaders": {
                            ml: "6px",
                            "div:last-of-type": {
                              right: "26px",
                              left: "unset !important",
                            },
                          },
                          ".DayPickerNavigation": {
                            display: "flex",
                            justifyContent: "space-between",
                            "div:last-of-type": {
                              position: "relative",
                              right: "42px",
                            },
                          },

                          ".CalendarMonthGrid": {
                            left: "0px",
                          },
                          ".CalendarMonth_table": {
                            borderCollapse: "unset",
                            borderSpacing: "1px",
                          },
                          ".CalendarMonth_1": {
                            p: "0px 16px 0px 0px !important",
                          },
                          ".DayPicker_weekHeader_1": {
                            p: "0px 16px 0px 0px !important",
                          },
                        }}
                      >
                        <DateRangePicker
                          startDate={startDate}
                          startDateId="start-date"
                          endDate={endDate}
                          endDateId="end-date"
                          onDatesChange={handleDatesChange}
                          focusedInput={focusedInput}
                          onFocusChange={(input) => setFocusedInput(input)}
                          noBorder
                          isOutsideRange={() => false}
                          displayFormat="ddd, Do MMM"
                          showClearDates
                          reopenPickerOnClearDates
                          startDatePlaceholderText="Explore from"
                          endDatePlaceholderText="Explore until"
                          hideKeyboardShortcutsPanel
                          firstDayOfWeek={1}
                          orientation={
                            lessThanMD
                              ? ("verticalScrollable" as OrientationShape)
                              : ("horizontal" as OrientationShape)
                          }
                          calendarInfoPosition="bottom"
                          numberOfMonths={lessThanMD ? 25 : 2}
                          withPortal={lessThanMD}
                          readOnly={lessThanMD}
                          withFullScreenPortal={lessThanMD}
                          daySize={lessThanMD ? 50 : 55}
                          navNext={
                            lessThanMD ? null : (
                              <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                aria-label="Next month"
                                sx={{
                                  // m: "14px",
                                  p: "3px",
                                  borderRadius: "8px",
                                  minWidth: "unset",
                                  zIndex: 1,
                                  bgcolor: "#e30053",
                                  color: "#fff",
                                  border: "1px solid",
                                  borderColor: "#fff",
                                  boxShadow: "0px 0px 7px 2px rgb(0 0 0 / 19%)",
                                  "&:hover": {
                                    bgcolor: "#fff",
                                    color: "#e30053",
                                    borderColor: "#e30053",
                                  },
                                  svg: {
                                    fontSize: "1.5rem",
                                  },
                                }}
                              >
                                <ArrowRightIcon />
                              </Button>
                            )
                          }
                          navPrev={
                            lessThanMD ? null : (
                              <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                aria-label="Previous month"
                                sx={{
                                  // m: "14px",
                                  p: "3px",
                                  borderRadius: "8px",
                                  minWidth: "unset",
                                  zIndex: 1,
                                  bgcolor: "#e30053",
                                  color: "#fff",
                                  border: "1px solid",
                                  borderColor: "#fff",
                                  boxShadow: "0px 0px 7px 2px rgb(0 0 0 / 19%)",
                                  "&:hover": {
                                    bgcolor: "#fff",
                                    color: "#e30053",
                                    borderColor: "#e30053",
                                  },
                                  svg: {
                                    fontSize: "1.5rem",
                                  },
                                }}
                              >
                                <ArrowLeftIcon />
                              </Button>
                            )
                          }
                          renderDayContents={(day) => (
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "flex-end",
                                p: "6px",
                                fontSize: "1.1rem",
                                borderRadius: "8px",
                                border: "1px solid #ddd",
                              }}
                            >
                              {moment(day).format("D")}
                            </Box>
                          )}
                          renderCalendarInfo={() => (
                            <CalendarInfo
                              applyHandler={() => setFocusedInput(null)}
                              clearDatesHandler={() => {
                                setFocusedInput("startDate");
                                handleDatesChange({ startDate: null, endDate: null });
                              }}
                              departureDate={watch()?.leavingStartDate}
                              retureDate={watch()?.leavingEndDate}
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  </CommonFieldUI>
                </Grid>

                <Grid
                  size={{ xs: 12, md: 6, lg: isShowAdvanceFields ? 3.9 : 2.9 }}
                  order={isShowAdvanceFields ? 4 : 3}
                >
                  <CommonFieldUI icon={<HarbourPortIcon />} label="Embarking Port">
                    <Box
                      sx={{
                        ".MuiInputBase-root.Mui-focused.MuiAutocomplete-inputRoot": {
                          "&::after": {
                            borderBottom: "none",
                          },
                          "&::before": {
                            borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                          },
                          input: {
                            p: "3px 0px 0px 0px",
                            fontSize: "15px",
                            fontFamily: "'blauerNue', 'blauerNue Fallback'",
                          },
                        },
                      }}
                    >
                      <Controller
                        name="departurePort"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            onFocus={() => setFocused(true)}
                            disablePortal
                            options={filteredDeparturePorts}
                            fullWidth
                            onInputChange={(e, value) => setPortSearchQuery(value)}
                            onChange={(e, val) => {
                              if (val) {
                                const port = {
                                  country: val.country,
                                  name: val.name,
                                  id: val.id,
                                  operator: val.operator,
                                };
                                setValue("departurePort", port);
                              } else {
                                setValue("departurePort", null);
                              }
                            }}
                            value={field.value}
                            getOptionLabel={(option) => `${option.name}, ${option.country}`}
                            filterOptions={(x) => x}
                            groupBy={(option) => option.country}
                            renderGroup={(params) => customAutocompleteRenderGroup(params)}
                            renderInput={(params) => (
                              <TextField
                                label=""
                                placeholder="Boarding port or city"
                                variant="standard"
                                focused
                                {...params}
                              />
                            )}
                            renderOption={(props, option, state) => {
                              const uniqueKey = `${option.id}-${option.country}-${option.name}`;
                              // eslint-disable-next-line @typescript-eslint/no-unused-vars
                              const { key, ...otherProps } = { ...props, key: uniqueKey };
                              return renderOptionPortsUI(
                                otherProps,
                                option,
                                state,
                                portSearchQuery,
                              );
                            }}
                            slotProps={{
                              paper: {
                                sx: {
                                  width: { xs: "calc(100% + 58px)", md: "300px" },
                                  boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                                  borderRadius: "0px 0px 14px 14px",
                                  ml: { xs: "-58px", md: "-35px" },
                                  mt: "3px",
                                },
                              },
                            }}
                            slots={{
                              popper: (props) => (
                                <Popper
                                  {...props}
                                  placement="bottom-start"
                                  modifiers={[
                                    {
                                      name: "flip",
                                      enabled: false,
                                    },
                                  ]}
                                />
                              ),
                            }}
                          />
                        )}
                      />
                    </Box>
                  </CommonFieldUI>
                </Grid>

                {isShowAdvanceFields && (
                  <>
                    <Grid size={{ xs: 12, md: 6, lg: 3.9 }} order={5}>
                      <CommonFieldUI icon={<CruiseLineIcon />} label="Ship Name">
                        <Box
                          sx={{
                            ".MuiInputBase-root.Mui-focused.MuiAutocomplete-inputRoot": {
                              "&::after": {
                                borderBottom: "none",
                              },
                              "&::before": {
                                borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                              },
                              input: {
                                p: "3px 0px 0px 0px",
                                fontSize: "15px",
                                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                              },
                            },
                          }}
                        >
                          <Controller
                            name="cruiseShip"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                {...field}
                                onFocus={() => setFocused(true)}
                                disablePortal
                                options={filteredCruiseShips}
                                fullWidth
                                onChange={(e, val) => {
                                  if (val) {
                                    const ship = {
                                      title: val.title,
                                      operator: {
                                        name: val.operator.name,
                                        profileImageHref: val.operator.profileImageHref,
                                      },
                                      href: val.href,
                                    };
                                    setValue("cruiseShip", ship);
                                  } else {
                                    setValue("cruiseShip", null);
                                  }
                                }}
                                value={field.value}
                                onInputChange={(e, value) => setShipSearchQuery(value)} //iput text
                                sx={{}}
                                getOptionLabel={(option) => `${option.title}`}
                                filterOptions={(x) => x}
                                groupBy={(option) => option.operator?.name}
                                renderGroup={(params) => customAutocompleteRenderGroup(params)}
                                renderInput={(params) => (
                                  <TextField
                                    label=""
                                    placeholder="Pick your ship"
                                    variant="standard"
                                    focused
                                    {...params}
                                  />
                                )}
                                renderOption={(props, option, state) =>
                                  renderOptionShipsUI(props, option, state, shipSearchQuery)
                                }
                                slotProps={{
                                  paper: {
                                    sx: {
                                      width: { xs: "calc(100% + 58px)", md: "300px" },
                                      boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                                      borderRadius: "0px 0px 14px 14px",
                                      ml: { xs: "-58px", md: "-36px" },
                                      mt: "3px",
                                    },
                                  },
                                }}
                                slots={{
                                  popper: (props) => (
                                    <Popper
                                      {...props}
                                      placement="bottom-start"
                                      modifiers={[
                                        {
                                          name: "flip",
                                          enabled: false,
                                        },
                                      ]}
                                    />
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                      </CommonFieldUI>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6, lg: 4.2 }} order={6}>
                      <CommonFieldUI icon={<CruiseTypeIcon />} label="Cruise Type">
                        <Box
                          sx={{
                            ".MuiInputBase-root.Mui-focused.MuiAutocomplete-inputRoot": {
                              "&::after": {
                                borderBottom: "none",
                              },
                              "&::before": {
                                borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
                              },
                              input: {
                                p: "3px 0px 0px 0px",
                                fontSize: "15px",
                                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                              },
                            },
                          }}
                        >
                          <Controller
                            name="cruiseType"
                            control={control}
                            render={({ field }) => (
                              <Autocomplete
                                {...field}
                                onFocus={() => setFocused(true)}
                                disablePortal
                                options={cruiseTypes}
                                fullWidth
                                onChange={(e, val) => {
                                  if (val) {
                                    const type = {
                                      label: val.label,
                                      name: val.name,
                                      value: val.value,
                                    };
                                    setValue("cruiseType", type);
                                  } else {
                                    setValue("cruiseType", null);
                                  }
                                }}
                                value={field.value}
                                sx={{}}
                                getOptionLabel={(option) => option.label}
                                renderInput={(params) => (
                                  <TextField
                                    label=""
                                    placeholder="Ocean or River Cruises?"
                                    variant="standard"
                                    focused
                                    {...params}
                                  />
                                )}
                                renderOption={(props, option, state) =>
                                  renderOptionUIV2(props, option, state)
                                }
                                slotProps={{
                                  paper: {
                                    sx: {
                                      width: { xs: "calc(100% + 58px)", md: "300px" },
                                      boxShadow: "0px 6px 16px rgba(0,0,0,0.2)",
                                      borderRadius: "0px 0px 14px 14px",
                                      ml: { xs: "-58px", md: "-12px" },
                                      mt: "3px",
                                    },
                                  },
                                }}
                                slots={{
                                  popper: (props) => (
                                    <Popper
                                      {...props}
                                      placement="bottom-start"
                                      modifiers={[
                                        {
                                          name: "flip",
                                          enabled: false,
                                        },
                                      ]}
                                    />
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                      </CommonFieldUI>
                    </Grid>
                  </>
                )}
              </Grid>

              <Grid
                container
                spacing={{ xs: 1.5, md: 2 }}
                columnSpacing={{ xs: 1, md: 2 }}
                size={{ xs: 12, sm: 12, md: 1.8 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation
                  // onClick={() => router.push("/cruise-results")}
                  sx={{
                    height: "100%",
                    width: "100%",
                    gap: "6px",
                    display: "flex",
                    textAlign: "left",
                    lineHeight: 1.2,
                    minHeight: { xs: "48px", md: "40px" },
                    fontFamily: "'blauerNue', 'blauerNue Fallback'",
                    fontWeight: 600,
                    fontSize: { xs: "14px", md: "18px" },
                    p: "10px",
                    flexDirection: "row",
                    ...(isShowAdvanceFields && {
                      flexDirection: { xs: "row", md: "column" },
                      gap: { xs: "6px", md: "8px" },
                      svg: {
                        width: { xs: "30px", md: "50px" },
                        height: { xs: "30px", md: "50px" },
                      },
                    }),
                  }}
                >
                  <SearchIcon sx={{ ml: "-4px" }} />
                  Search
                </Button>
              </Grid>
            </Grid>

            <Button
              disableElevation
              sx={{
                textTransform: "initial",
                position: "absolute",
                bottom: { xs: "10px", md: "-33px" },
                left: { xs: "6px", md: "18px" },
                display: "flex",
                alignItems: "center",
                width: "fit-content",
                p: { xs: "5px 10px 4px 4px", md: "3px 10px 4px 4px" },
                gap: "4px",
                borderRadius: "0px 0px 8px 8px",
                fontSize: { xs: "14px", md: "12px" },
                bgcolor: { xs: "unset", md: "primary.main" },
                color: { xs: "primary.main", md: "#fff" },
                "> div": {
                  mt: "1px",
                },
                "> svg": {
                  width: "20px",
                  height: "20px",
                },
              }}
              onClick={
                () => setIsShowAdvanceFields((prev) => !prev)
                // setFocused(false)
              }
            >
              <>{isShowAdvanceFields ? <CheckedIcon /> : <UncheckedIcon />}</>
              <Box>More Filters</Box>
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default SearchLayout;

const CommonFieldUI: React.FC<{
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, icon, children }) => {
  return (
    <Box sx={{ display: "flex", gap: "10px" }}>
      <Box
        sx={{
          height: "46px",
          width: "46px",
          display: "flex",
          flexShrink: 0,
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#EFEEFF",
          color: "#06009C",
          borderRadius: "10px",
          border: "1px solid #D2D0FF",
          svg: {
            width: "25px",
            height: "25px",
          },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            fontSize: { xs: "17px", md: "16px" },
            fontWeight: 600,
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
          }}
        >
          {label}
        </Box>
        <Box sx={{ width: "100%" }}>{children}</Box>
      </Box>
    </Box>
  );
};

export const renderOptionUI = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: {
    value?: number;
    label?: string;
    name?: string;
    title?: string;
    type?: string;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  state: AutocompleteRenderOptionState,
) => {
  const searchQuery = state.inputValue.toLowerCase();
  const displayText = option.title || option.label || option.name || "";

  // If no search query, just render the normal label with the Box styling
  if (!searchQuery) {
    return (
      <Box
        key={option.value}
        component={"li"}
        {...props}
        sx={{
          p: "8px 12px !important",
          fontWeight: 400,
          fontSize: "15px",
          bgcolor: "#fff",
          color: "#555",
          transition: "all 0.2s",
          borderBottom: "1px solid #ddd",
          "&:hover": {
            bgcolor: "#060273 !important",
            color: "#fff",
            cursor: "pointer",
          },
        }}
      >
        <Box>{displayText}</Box>
      </Box>
    );
  }

  // Split the label based on the search query to highlight matching parts
  const labelParts = displayText.split(new RegExp(`(${searchQuery})`, "gi"));

  return (
    <Box
      component={"li"}
      {...props}
      sx={{
        p: "8px 12px !important",
        fontWeight: 400,
        fontSize: "15px",
        bgcolor: "#fff",
        color: "#555",
        transition: "all 0.2s",
        borderBottom: "1px solid #ddd",
        "&:hover": {
          bgcolor: "#060273 !important",
          color: "#fff",
          cursor: "pointer",
        },
      }}
    >
      <Box>
        {labelParts.map((part: string, index: number) =>
          part.toLowerCase() === searchQuery.toLowerCase() ? (
            <span key={index} style={{ fontWeight: "bold", color: "#E30053" }}>
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          ),
        )}
      </Box>
    </Box>
  );
};

// For Destination Region Field
export const renderOptionUIV2 = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: {
    value?: number;
    label?: string;
    name?: string;
    title?: string;
    type?: string;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  state: AutocompleteRenderOptionState,
  icon?: React.ReactNode,
) => {
  const searchQuery = state.inputValue.toLowerCase();
  const displayText = option.title || option.label || option.name || "";

  // If no search query, just render the normal label with the Box styling
  if (!searchQuery) {
    return (
      <Box
        key={option.value}
        component={"li"}
        {...props}
        sx={{
          p: icon ? "10px 9px !important" : "10px 12px !important",
          fontWeight: 400,
          fontSize: "15px",
          bgcolor: "#fff",
          color: "#555",
          transition: "all 0.2s",
          borderBottom: "1px solid #ddd",
          "&:hover": {
            bgcolor: "#060273 !important",
            color: "#fff",
            cursor: "pointer",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            svg: {
              width: "20px",
              height: "20px",
              color: "#aaa",
            },
          }}
        >
          {icon ? icon : ""}
          <Box sx={{ height: "100%", mb: "-1px" }}>{displayText}</Box>
        </Box>
      </Box>
    );
  }

  // Split the label based on the search query to highlight matching parts
  const labelParts = displayText.split(new RegExp(`(${searchQuery})`, "gi"));

  return (
    <Box
      component={"li"}
      {...props}
      sx={{
        p: icon ? "10px 9px !important" : "10px 12px !important",
        fontWeight: 400,
        fontSize: "15px",
        bgcolor: "#fff",
        color: "#555",
        transition: "all 0.2s",
        borderBottom: "1px solid #ddd",
        "&:hover": {
          bgcolor: "#060273 !important",
          color: "#fff",
          cursor: "pointer",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          svg: {
            width: "20px",
            height: "20px",
            color: "#aaa",
          },
        }}
      >
        {icon ? icon : ""}
        <Box sx={{ height: "100%", mb: "-1px" }}>
          {labelParts.map((part: string, index: number) =>
            part.toLowerCase() === searchQuery.toLowerCase() ? (
              <span key={index} style={{ fontWeight: "bold", color: "#E30053" }}>
                {part}
              </span>
            ) : (
              <span key={index}>{part}</span>
            ),
          )}
        </Box>
      </Box>
    </Box>
  );
};

// render on port
const renderOptionPortsUI = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: {
    country: string;
    name: string;
    id: number;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  state: AutocompleteRenderOptionState,
  searchQuery: string,
) => {
  const uniqueKey = `${option.id}-${option.country}-${option.name}`;

  // Function to highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query) return <span>{text}</span>;

    const lowercaseText = text.toLowerCase();
    const lowercaseQuery = query.toLowerCase();
    const index = lowercaseText.indexOf(lowercaseQuery);

    if (index === -1) return <span>{text}</span>;

    return (
      <>
        {text.substring(0, index)}
        <span style={{ color: "#E30053", fontWeight: 600 }}>
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };

  return (
    <Box
      key={uniqueKey}
      component={"li"}
      {...props}
      sx={{
        p: "10px 9px !important",
        fontWeight: 400,
        fontSize: "15px",
        bgcolor: "#fff",
        color: "#555",
        transition: "all 0.2s",
        borderBottom: "1px solid #ddd",
        "&:hover": {
          bgcolor: "#060273 !important",
          color: "#fff",
          cursor: "pointer",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          svg: {
            width: "20px",
            height: "20px",
            color: "#aaa",
          },
        }}
      >
        <SubArrowIcon sx={{}} />
        <Box sx={{ height: "100%", mb: "-1px" }}>
          {highlightMatch(option.name, searchQuery)}
          {/* {option.country ? `,` : ""} {highlightMatch(option.country, searchQuery)} */}
        </Box>
      </Box>
    </Box>
  );
};

// render on ships
const renderOptionShipsUI = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: {
    title: string;
    operator: {
      name: string;
      profileImageHref: string;
    };
    href: string;
    // value?: number;
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  state: AutocompleteRenderOptionState,
  searchQuery: string,
) => {
  const uniqueKey = `${option.title}-${option.href}`;
  const highlightMatch = (text: string, query: string) => {
    if (!query) return <span>{text}</span>;

    const lowercaseText = text.toLowerCase();
    const lowercaseQuery = query.toLowerCase();
    const index = lowercaseText.indexOf(lowercaseQuery);

    if (index === -1) return <span>{text}</span>;

    return (
      <>
        {text.substring(0, index)}
        <span style={{ color: "#E30053", fontWeight: 600 }}>
          {text.substring(index, index + query.length)}
        </span>
        {text.substring(index + query.length)}
      </>
    );
  };
  return (
    <Box
      key={uniqueKey}
      component={"li"}
      {...props}
      sx={{
        p: "10px 9px !important",
        fontWeight: 400,
        fontSize: "15px",
        bgcolor: "#fff",
        color: "#555",
        transition: "all 0.2s",
        borderBottom: "1px solid #ddd",
        "&:hover": {
          bgcolor: "#060273 !important",
          color: "#fff",
          cursor: "pointer",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          svg: {
            width: "20px",
            height: "20px",
            color: "#aaa",
          },
        }}
      >
        <SubArrowIcon sx={{}} />
        <Box sx={{ height: "100%", mb: "-1px" }}>{highlightMatch(option.title, searchQuery)}</Box>
      </Box>
    </Box>
  );
};

const customAutocompleteRenderGroup = (params: AutocompleteRenderGroupParams) => {
  return (
    <Box key={params.key}>
      <Box>
        {params.group && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderBottom: "1px solid #ddd",
              p: "4px 12px",
              bgcolor: "#efeeff",
              color: "#060273",
            }}
          >
            {/* country */}
            <Box
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                span: {
                  textTransform: "capitalize",
                  fontWeight: 500,
                },
              }}
            >
              {params.group}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            bgcolor: "#fff",
            display: "grid",
            alignItems: "center",
          }}
        >
          {params.children}
        </Box>
      </Box>
    </Box>
  );
};

const destinationAutocompleteRenderGroup = (params: AutocompleteRenderGroupParams) => {
  return (
    <Box key={params.key}>
      <Box sx={{}}>
        {params.group && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              borderBottom: "1px solid #ddd",
              p: "4px 12px",
              bgcolor: "#efeeff",
              color: "#060273",
            }}
          >
            {/* country */}
            <Box
              component="span"
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                span: {
                  textTransform: "capitalize",
                  fontWeight: 500,
                },
              }}
            >
              Explore Regions by <span>{params.group}</span> {params.group == "river" ? "🚤" : "🌊"}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            bgcolor: "#fff",
            display: "grid",
            alignItems: "center",
          }}
        >
          {params.children}
        </Box>
      </Box>
    </Box>
  );
};

export const CalendarInfo: React.FC<{
  applyHandler: () => void;
  clearDatesHandler: () => void;

  departureDate: string | null;
  retureDate?: string | null;
  depOnly?: boolean;
}> = ({ applyHandler, clearDatesHandler, departureDate, retureDate }) => {
  const isMobile = useMediaQuery("(max-width: 900px)");
  return (
    <>
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            position: "absolute",
            top: "0px",
            width: "100%",
            left: "0px",
            zIndex: 1400,
            p: "13px",
            bgcolor: "#fff",
            "> div": {
              border: "1px solid #00a699",
              p: "4px 7px 5px",
              borderRadius: "8px",
              lineHeight: 1.1,
              bgcolor: "#c3e5ff",
            },
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                fontWeight: 500,
                color: "#00a699",
                fontSize: "12px",
                mb: "4px",
                mt: "2px",
              }}
            >
              Embarking on
            </Box>
            <Box>
              {departureDate == null || departureDate == "" ? (
                <Box sx={{ color: "#e30053", fontWeight: "300", fontSize: "17px" }}>
                  Explore from
                </Box>
              ) : (
                <Box sx={{ fontWeight: 600, color: "#e30053", fontSize: "17px" }}>
                  {moment(departureDate).format("DD/MM/YYYY")}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                fontWeight: 500,
                color: "#00a699",
                fontSize: "12px",
                mb: "4px",
                mt: "2px",
              }}
            >
              Embarking from
            </Box>
            <Box>
              {retureDate == null || retureDate == "" ? (
                <Box sx={{ color: "#e30053", fontWeight: "300", fontSize: "17px" }}>
                  Explore until
                </Box>
              ) : (
                <Box sx={{ fontWeight: 600, color: "#e30053", fontSize: "17px" }}>
                  {moment(retureDate).format("DD/MM/YYYY")}
                </Box>
              )}
            </Box>
          </Box>
          <Button
            onClick={applyHandler}
            aria-label="Close"
            sx={{
              minWidth: "unset",
              borderRadius: "8px",
              p: "8px 10px",
              zIndex: 1,
              "&:hover": {
                bgcolor: "#fff",
                border: "1p solid #ccc",
                color: "#000",
                boxShadow: "0px 0px 8px rgba(0,0,0,0.3)",
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      )}

      <Box
        sx={{
          p: { xs: "13px", md: "16px" },
          mx: "-16px",
          borderTop: "1px solid #ccc",
          display: "flex",
          gap: "20px",
          bgcolor: "#fff",
          zIndex: 2,
          justifyContent: "flex-end",
          ...(isMobile && {
            position: "fixed",
            bottom: "0px",
            width: "100%",
            ml: "0px",
          }),
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button
            disableElevation
            variant="contained"
            sx={{
              bgcolor: "#fff",
              color: "#e30053",
              borderRadius: "6px",
              padding: "6px 0px 4px",
              border: "1px solid #e30053",
              fontWeight: 500,
              fontSize: "13px",
              minHeight: "unset",
              maxHeight: "40px",
              height: "100%",
              width: { xs: "100%", md: "fit-content" },
              boxShadow: "none",
            }}
            onClick={() => clearDatesHandler()}
          >
            Clear
          </Button>

          <Button
            disableElevation
            variant="contained"
            sx={{
              color: "#fff",
              bgcolor: "#e30053",
              borderRadius: "6px",
              padding: "6px 0px 4px",
              fontWeight: 500,
              fontSize: "13px",
              minHeight: "unset",
              maxHeight: "40px",
              width: { xs: "100%", md: "fit-content" },
              height: "100%",
              boxShadow: "none",
            }}
            onClick={applyHandler}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </>
  );
};
